import {TextBuilder} from './builders/TextBuilder'
import {NumberBuilder} from './builders/NumberBuilder'
import {LinkBuilder} from './builders/LinkBuilder'
import {ArrayBuilder} from './builders/ArrayBuilder'
import {ObjectBuilder} from './builders/ObjectBuilder'
import {BooleanBuilder} from './builders/BooleanBuilder'
import {TextualDataBuilder} from './builders/TextualDataBuilder'
import type {IBaseData} from './builders/BaseDataBuilder'
import {DATA} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import
import type {Builder, Pointer} from '@wix/document-services-types'
import {SlotDataBuilder} from './builders/SlotDataBuilder'
import {generateWidgetSelector} from '../../builderManifest'

interface AjvBaseResolvedSchema {
    $ref: string
    $id: string
    type: string
    description: string
    title: string
    default: any
    properties?: Record<string, AjvBaseResolvedSchema>
    maxLength?: number
    minimum?: number
    maximum?: number
    multipleOf?: number
    minLength?: number
    minItems?: number
    items?: AjvBaseResolvedSchema
}

export class DataBuilderFactory {
    constructor() {}

    createDataBuilder(propertyName: string, propertySchema: AjvBaseResolvedSchema): IBaseData | undefined {
        const displayName = propertySchema.title

        let dataType = propertySchema.$ref
        if (propertySchema.properties) {
            dataType = 'object'
        } else if (propertySchema.type === 'array') {
            dataType = 'array'
        }

        switch (dataType) {
            case 'text':
                return new TextBuilder(displayName, propertySchema.default, propertySchema.maxLength)
            case 'number':
                return new NumberBuilder(
                    displayName,
                    propertySchema.default,
                    propertySchema.minimum,
                    propertySchema.maximum,
                    propertySchema.multipleOf
                )
            case 'url':
                return new LinkBuilder(displayName, propertySchema.default)
            case 'array':
                if (!propertySchema.items) {
                    console.error('Items are required for array')
                    return
                }
                const itemsRef = propertySchema.items?.$ref || DATA.DATA_TYPE.text
                const innerDisplayName = propertySchema.items?.title || propertyName
                const innerData = this.createDataBuilder(innerDisplayName, {
                    ...propertySchema.items,
                    title: innerDisplayName,
                    $ref: itemsRef
                })

                if (!innerData) {
                    console.error('Items are required for array')
                    return
                }

                return new ArrayBuilder(displayName, propertySchema.default, innerData)
            case 'object':
                return new ObjectBuilder(displayName, propertySchema.default, propertySchema.properties)
            case 'boolean':
                return new BooleanBuilder(displayName, propertySchema.default)
            case 'dateTime':
            case 'image':
                return new TextualDataBuilder(dataType, displayName, propertySchema.default)
            default:
                console.error(`Unsupported data type: ${dataType}`)
        }
    }
    /**
     * Slots it's unique usecase when we should create Builder.DataItem for each of slot
     * Because in this property will be passed plugin React.Component and rendered
     * Doc: https://dev.wix.com/docs/fed-guild/articles/builder/data#reactelementcontainer-type
     */
    getSlotData(slotCompPointer: Pointer): Builder.DataItem {
        return new SlotDataBuilder({
            selector: generateWidgetSelector([slotCompPointer.id]),
            slotId: slotCompPointer.id
        }).toBuilderDataItem()
    }

    propertySchemaToBuilderData(propertySchemaMap: Record<string, AjvBaseResolvedSchema>): Builder.Data {
        return Object.entries(propertySchemaMap).reduce((acc, [propertyName, propertySchema]) => {
            const dataBuilder = this.createDataBuilder(propertyName, propertySchema)
            if (!dataBuilder) {
                return acc
            }
            return {
                ...acc,
                [propertyName]: dataBuilder.toBuilderDataItem()
            }
        }, {})
    }
}
